/**
 * Cliente HTTP tipado.
 *
 * Usa ``openapi-fetch`` ate o schema.ts ser gerado pelo
 * `pnpm gen:api`. Por enquanto, exporta um helper ``api.request``
 * minimo + ``api.GET``/``api.POST`` para uso direto.
 *
 * Auth refresh: quando uma chamada autenticada recebe 401, o cliente
 * tenta UMA renovacao silenciosa via ``POST /auth/refresh`` (com o
 * token expirado no header) e refaz a request original com o novo
 * token. Se o refresh tambem falhar, ai sim limpa o token e
 * redireciona para /login. Evita logout abrupto no meio da sessao
 * causado pelo TTL de 8h do JWT.
 */

import { env } from "@/lib/env";

import { tokenStorage } from "@/auth/tokenStorage";

import type { ApiErrorBody } from "./types";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: ApiErrorBody | { detail: string } | null,
    message?: string,
  ) {
    super(message ?? `HTTP ${status}`);
    this.name = "ApiError";
  }
}

async function buildHeaders(extra?: HeadersInit): Promise<Headers> {
  const headers = new Headers(extra);
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  const token = await tokenStorage.getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}

// Singleton in-flight para coalescer multiplas requests que falham em
// 401 simultaneamente -- todas esperam o mesmo refresh em vez de
// disparar N requests redundantes contra ``/auth/refresh``.
let inFlightRefresh: Promise<string | null> | null = null;

async function attemptRefresh(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;
  inFlightRefresh = (async () => {
    const token = await tokenStorage.getToken();
    if (!token) return null;
    try {
      const resp = await fetch(`${env.API_BASE_URL}/auth/refresh`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!resp.ok) return null;
      const body = (await resp.json()) as { access_token?: string };
      const newToken = body.access_token;
      if (!newToken) return null;
      await tokenStorage.setToken(newToken);
      return newToken;
    } catch {
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

// Endpoints que NAO devem disparar refresh em 401:
// - ``/auth/refresh``: defensivo (o proprio attemptRefresh ja usa
//   ``fetch`` cru e nao passa por ``fetchWithAuth``, mas mantemos
//   excluido para garantir).
// - ``/auth/sso/*``: endpoints publicos -- 401 ai significa OIDC ruim,
//   nao token expirado.
// ``/auth/me`` DEVE permanecer elegivel: e a primeira chamada que
// dispara depois do token expirar em background, e onde o refresh
// silencioso evita o redirect indesejado para /login.
const NO_REFRESH_PATHS = new Set([
  "/auth/refresh",
  "/auth/sso/login",
  "/auth/sso/callback",
]);

function shouldAttemptRefresh(path: string): boolean {
  for (const skip of NO_REFRESH_PATHS) {
    if (path.startsWith(skip)) return false;
  }
  return true;
}

async function handleUnauthorized(): Promise<void> {
  tokenStorage.clearToken();
  if (
    typeof window !== "undefined" &&
    !window.location.pathname.startsWith("/login")
  ) {
    window.location.assign("/login");
  }
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Executa a request com 1 retry automatico em 401 via ``/auth/refresh``.
 * ``execute`` recebe o token atual (ou null) e retorna a Response.
 * Quando a primeira tentativa volta 401 num path elegivel, tenta
 * renovar o token e refaz a request com o novo. Falha do refresh
 * sai pelo caminho 401 normal (clearToken + redirect /login).
 */
async function fetchWithAuth<T = unknown>(
  path: string,
  execute: () => Promise<Response>,
): Promise<T> {
  let response = await execute();
  if (response.status === 401 && shouldAttemptRefresh(path)) {
    const newToken = await attemptRefresh();
    if (newToken) {
      response = await execute();
    }
  }
  if (response.status === 401) {
    await handleUnauthorized();
    const body = await readBody(response);
    throw new ApiError(401, body as ApiErrorBody);
  }
  if (response.status === 204) return undefined as T;
  const body = await readBody(response);
  if (!response.ok) {
    throw new ApiError(response.status, body as ApiErrorBody);
  }
  return body as T;
}

export const api = {
  baseUrl: env.API_BASE_URL,

  async GET<T = unknown>(
    path: string,
    init?: RequestInit & { params?: Record<string, string | number | boolean> },
  ): Promise<T> {
    const url = new URL(path.replace(/^\//, ""), `${env.API_BASE_URL}/`);
    if (init?.params) {
      for (const [k, v] of Object.entries(init.params)) {
        url.searchParams.set(k, String(v));
      }
    }
    return fetchWithAuth<T>(path, async () =>
      fetch(url, {
        ...init,
        method: "GET",
        headers: await buildHeaders(init?.headers),
      }),
    );
  },

  async POST<T = unknown>(
    path: string,
    body?: unknown,
    init?: RequestInit,
  ): Promise<T> {
    return fetchWithAuth<T>(path, async () =>
      fetch(`${env.API_BASE_URL}${path}`, {
        ...init,
        method: "POST",
        headers: await buildHeaders({
          "Content-Type": "application/json",
          ...((init?.headers as Record<string, string>) ?? {}),
        }),
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    );
  },

  async PATCH<T = unknown>(
    path: string,
    body?: unknown,
    init?: RequestInit,
  ): Promise<T> {
    return fetchWithAuth<T>(path, async () =>
      fetch(`${env.API_BASE_URL}${path}`, {
        ...init,
        method: "PATCH",
        headers: await buildHeaders({
          "Content-Type": "application/json",
          ...((init?.headers as Record<string, string>) ?? {}),
        }),
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    );
  },

  async PUT<T = unknown>(
    path: string,
    body?: unknown,
    init?: RequestInit,
  ): Promise<T> {
    return fetchWithAuth<T>(path, async () =>
      fetch(`${env.API_BASE_URL}${path}`, {
        ...init,
        method: "PUT",
        headers: await buildHeaders({
          "Content-Type": "application/json",
          ...((init?.headers as Record<string, string>) ?? {}),
        }),
        body: body !== undefined ? JSON.stringify(body) : undefined,
      }),
    );
  },

  async DELETE<T = unknown>(path: string, init?: RequestInit): Promise<T> {
    return fetchWithAuth<T>(path, async () =>
      fetch(`${env.API_BASE_URL}${path}`, {
        ...init,
        method: "DELETE",
        headers: await buildHeaders(init?.headers),
      }),
    );
  },
};
